/**
 * A LIGHT in-app confirmation — the product's own dialog for actions that need
 * a deliberate click but are NOT destructive.
 *
 * ## Why this exists next to `ConfirmDestructiveDialog`
 *
 * That one gates on a TYPED PHRASE, which is the right friction for an action
 * that cannot be walked back. Restoring a case from the Lixeira is the
 * opposite: it puts something back. Reusing the typed-phrase dialog there would
 * charge a destructive-grade toll for a recoverable act, and the friction would
 * stop meaning anything where it IS deserved.
 *
 * ## What it replaces, and why that mattered
 *
 * Native `window.confirm()`. Found by `ui-tester` 2026-09-04, twice over:
 *
 * 1. It **blocks the renderer**. A driver watching the page sees a frozen tab,
 *    not a prompt — the run that hit it spent a whole window diagnosing "Chrome
 *    is wedged" before finding a dialog underneath. A lawyer would say "it
 *    froze". Nothing in-page can render, log, or recover while it is up.
 * 2. It **cannot be themed or fully translated**. The message goes through
 *    `t()`, but the BUTTONS are the browser's, in the browser's language — so a
 *    pt-BR lawyer reads a Portuguese question with English OK/Cancel.
 *
 * ⚠ Scope note (`ide`, 2026-09-04): LexIDE has EIGHT native `confirm()` sites
 * and **none of them is destructive** — archive preserves the record by its own
 * copy, revoke re-pairs, cancel stops work without deleting. So there is no
 * typed-phrase rule being violated anywhere, and this component is a
 * consistency-and-observability fix, not a safety one. The other seven sites are
 * deliberately NOT converted here; sweeping them is a UX change the triage that
 * routed this one did not cover.
 */

import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import {
  dialogOverlayCls,
  dialogPanelCls,
  dialogCancelBtnCls,
} from "@/components/context-pane/dialog-styles";

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  /** Dialog heading — e.g. "Restaurar caso". */
  title: string;
  /** One sentence naming what will happen. Localized by the CALLER. */
  description: string;
  confirmButtonLabel: string;
  cancelButtonLabel: string;
  onConfirm: () => Promise<void> | void;
  /** Disables both buttons and shows the wait cursor while the action runs. */
  busy?: boolean;
  /** Test hook; defaults to `confirm-dialog`. */
  testId?: string;
}

export function ConfirmDialog({
  open,
  onClose,
  title,
  description,
  confirmButtonLabel,
  cancelButtonLabel,
  onConfirm,
  busy = false,
  testId = "confirm-dialog",
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  // Focus the CONFIRM button on open, so a keyboard user can accept with one
  // keystroke — the same affordance the native confirm() gave them. Safe here
  // and ONLY here: this dialog is for recoverable actions. The destructive
  // dialog deliberately does the opposite and focuses its phrase input.
  useEffect(() => {
    if (!open) return;
    // Remember who opened us so focus can go home on close. A native confirm()
    // did this for free, and losing it would be a regression dressed as an
    // improvement. ⚠ Read at OPEN, and checked for `isConnected` before use —
    // an opener that has since unmounted is a dead node, and focusing one
    // silently drops the caret to <body>.
    openerRef.current = document.activeElement as HTMLElement | null;
    const id = window.setTimeout(() => confirmRef.current?.focus(), 60);
    return () => {
      window.clearTimeout(id);
      const opener = openerRef.current;
      if (opener?.isConnected) opener.focus();
    };
  }, [open]);

  // Esc closes — a native confirm() gave the lawyer this for free and losing it
  // in the replacement would be a regression dressed as an improvement.
  useEffect(() => {
    if (!open || busy) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      // Keep Tab inside the dialog. `aria-modal` tells assistive tech the page
      // behind is inert; it does not MAKE it inert, so without this a keyboard
      // user tabs straight out of a modal into the page it is covering. Two
      // focusables, so the cycle is explicit rather than a generic trap.
      if (e.key !== "Tab") return;
      const first = cancelRef.current;
      const last = confirmRef.current;
      if (!first || !last) return;
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (active !== first && active !== last) {
        // Focus escaped (or never landed) — pull it back to a known control.
        e.preventDefault();
        last.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  return (
    <div className={cn(dialogOverlayCls, busy && "cursor-wait")}>
      <button
        type="button"
        aria-label={cancelButtonLabel}
        onClick={busy ? undefined : onClose}
        disabled={busy}
        className={cn("absolute inset-0 cursor-default", busy && "cursor-wait")}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        data-testid={testId}
        className={cn(dialogPanelCls, "relative")}
      >
        <h2
          id="confirm-dialog-title"
          className="text-sm font-semibold text-slate-900 dark:text-slate-100"
        >
          {title}
        </h2>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{description}</p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid={`${testId}-cancel`}
            className={dialogCancelBtnCls}
          >
            {cancelButtonLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={() => void onConfirm()}
            disabled={busy}
            data-testid={`${testId}-confirm`}
            className="rounded bg-coffee-700 px-3 py-1.5 text-sm font-medium text-cream-50 hover:bg-coffee-800 disabled:opacity-50 dark:bg-coffee-600 dark:hover:bg-coffee-700"
          >
            {confirmButtonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
